import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, IconButton, ToggleSwitchField, Card } from '@dopomogai/ui';
import { FileDown, Download, Copy, ChevronDown, Code, FileJson } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlideData, PresentationTheme } from '@dopomogai/types';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides: SlideData[];
  presentationTitle: string;
  theme: PresentationTheme;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  slides,
  presentationTitle,
  theme,
}) => {
  const { t } = useTranslation(['export', 'common']);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState<'jsx' | 'json' | null>(null);

  const handleExportPdf = () => {
    // This is a placeholder for the logic that will toggle a 'printable' class on the body
    // and then call window.print(). The actual logic should live in the main App.tsx
    // to control the entire page's appearance.
    alert(`Printing to PDF... (Include Notes: ${includeNotes})`);
    window.print();
    onClose();
  };

  const generateExportContent = (format: 'jsx' | 'json'): string => {
    if (format === 'json') {
      const exportData = {
        title: presentationTitle,
        theme: theme,
        slides: slides.map(({ id, title, type, content, notes, order }) => ({
          id, title, type, content, order, ...(includeNotes && { notes }),
        })),
      };
      return JSON.stringify(exportData, null, 2);
    }
    // JSX Export
    const slideComponents = slides.map(s => `<Slide>${s.content}${includeNotes && s.notes ? `\n<Notes>${s.notes}</Notes>\n` : ''}</Slide>`).join('\n');
    return `import { Deck, Slide, Notes, ...AllSpectacleComponents } from 'spectacle';
    
const theme = ${JSON.stringify(theme, null, 2)};

const MyPresentation = () => (
  <Deck theme={theme}>
    ${slideComponents}
  </Deck>
);
    `;
  };

  const handleCopy = async (format: 'jsx' | 'json') => {
    const content = generateExportContent(format);
    await navigator.clipboard.writeText(content);
    setCopied(format);
    setTimeout(() => setCopied(null), 2000);
  };
  
  const handleDownload = (format: 'jsx' | 'json') => {
    const content = generateExportContent(format);
    const filename = `${presentationTitle.replace(/\s+/g, '-')}.${format === 'jsx' ? 'jsx' : 'json'}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', 'Export Presentation')}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} text={t('common:cancel', 'Cancel')} />
          <Button
            variant="primary"
            onClick={handleExportPdf}
            icon={<FileDown size={16} />}
            text={t('export_as_pdf', 'Export as PDF')}
          />
        </>
      }
    >
      <div className="space-y-6">
        {/* Primary PDF Export Section */}
        <div>
            <h3 className="text-lg font-bold text-slate-800 mb-3">{t('pdf_options_title', 'PDF Options')}</h3>
            <p className="text-sm text-slate-600 mb-4">{t('pdf_options_desc', 'Use your browser\'s "Save as PDF" function in the print dialog for the best result.')}</p>
            <ToggleSwitchField
                label={t('include_speaker_notes', 'Include Speaker Notes')}
                descriptionText={t('speaker_notes_desc', 'Notes will be added on a separate page after each slide.')}
                checked={includeNotes}
                onChange={(e) => setIncludeNotes(e.target.checked)}
            />
        </div>
        
        {/* Developer / Advanced Section */}
        <div className="border-t-2 border-black pt-6">
           <button
             onClick={() => setShowAdvanced(!showAdvanced)}
             className="w-full flex justify-between items-center font-bold text-slate-800 hover:text-black"
           >
             <span>{t('advanced_exports_title', 'Developer Exports')}</span>
             <motion.div animate={{ rotate: showAdvanced ? 180 : 0 }}>
                <ChevronDown size={20} />
             </motion.div>
           </button>

            <AnimatePresence>
                {showAdvanced && (
                    <motion.div
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{ height: 'auto', opacity: 1, marginTop: '1.5rem' }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card title="React (JSX)" icon={<Code />} noHoverEffect>
                            <p className="text-sm text-slate-600 mb-4">{t('jsx_desc', 'Get the full presentation as a single React component file.')}</p>
                            <div className="flex gap-2">
                                <Button fullWidth size="sm" variant="secondary" icon={<Copy size={14} />} text={copied === 'jsx' ? t('common:copied') : t('common:copy')} onClick={() => handleCopy('jsx')} />
                                {/* FIX: Added ariaLabel */}
                                <IconButton icon={<Download size={14} />} onClick={() => handleDownload('jsx')} tooltip="Download JSX" ariaLabel="Download JSX file" />
                            </div>
                        </Card>
                        <Card title="Project (JSON)" icon={<FileJson />} noHoverEffect>
                            <p className="text-sm text-slate-600 mb-4">{t('json_desc', 'Save the entire project data, including slides and theme, as a JSON file.')}</p>
                            <div className="flex gap-2">
                                <Button fullWidth size="sm" variant="secondary" icon={<Copy size={14} />} text={copied === 'json' ? t('common:copied') : t('common:copy')} onClick={() => handleCopy('json')} />
                                {/* FIX: Added ariaLabel */}
                                <IconButton icon={<Download size={14} />} onClick={() => handleDownload('json')} tooltip="Download JSON" ariaLabel="Download JSON file" />
                            </div>
                        </Card>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
      </div>
    </Modal>
  );
};